import React from 'react';
import { Box, keyframes, SxProps, Theme } from '@mui/material';

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px); 
    filter: blur(5px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
    filter: blur(0);
  }
`;

interface PageTransitionProps {
  children: React.ReactNode;
  disabled?: boolean;
}

/**
 * PageTransition component that provides a "Staggered Reveal" effect to its children.
 * It automatically applies a delayed fadeInUp animation to each direct child.
 */
export const PageTransition: React.FC<PageTransitionProps> = ({ children, disabled = false }) => {
  if (disabled) return <>{children}</>;

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%',
        // Target all direct children for the staggered reveal
        '& > *': {
          animation: `${fadeInUp} 0.8s cubic-bezier(0.215, 0.61, 0.355, 1) forwards`,
          animationFillMode: 'backwards',
          willChange: 'transform, opacity, filter',
        },
        // Apply staggered delays
        '& > *:nth-of-type(1)': { animationDelay: '0.1s' },
        '& > *:nth-of-type(2)': { animationDelay: '0.25s' },
        '& > *:nth-of-type(3)': { animationDelay: '0.4s' },
        '& > *:nth-of-type(4)': { animationDelay: '0.55s' },
        '& > *:nth-of-type(5)': { animationDelay: '0.7s' },
        '& > *:nth-of-type(6)': { animationDelay: '0.85s' },
        '& > *:nth-of-type(7)': { animationDelay: '1.0s' },
        '& > *:nth-of-type(8)': { animationDelay: '1.15s' },
        '@media (prefers-reduced-motion: reduce)': {
          '& > *': {
            animation: 'none',
            willChange: 'auto',
          },
        },
      }}
    >
      {children}
    </Box>
  );
};

interface StaggeredRevealProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  sx?: SxProps<Theme>;
}

/**
 * Individual StaggeredReveal component for more granular control within a page.
 */
export const StaggeredReveal: React.FC<StaggeredRevealProps> = ({ 
  children, 
  delay = 0, 
  duration = 0.8,
  sx 
}) => {
  return (
    <Box
      sx={{
        animation: `${fadeInUp} ${duration}s cubic-bezier(0.215, 0.61, 0.355, 1) ${delay}s forwards`,
        animationFillMode: 'backwards',
        willChange: 'transform, opacity',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          willChange: 'auto',
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

export default PageTransition;
